import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Center, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertOctagonFilled } from '@tabler/icons-react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { clearSessionExpiry, logoutSession } from '../api/auth';
import { ApiError } from '../api/http';
import {
  submitVoucherDocuments,
  submitVoucherSubmission,
  uploadVoucherDocuments,
  validateVoucherEligibility,
  type VoucherDocumentsResponse,
  VOUCHER_NOT_RECORDED_ERROR,
  VoucherApiError,
} from '../api/vouchers';
import { systemErrorAlert, type FormAlertState } from '../components/FormAlert';
import FormField from '../components/FormField';
import PageShell from '../components/PageShell';
import { useProtectedSession } from '../hooks/useProtectedSession';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import type { FormPageConfig, ProgramConfig, SectionSchema, ThemeConfig } from '../types/schema';
import { isDevelopmentEnvironment } from '../utils/environment';
import { buildReturnToPath } from '../utils/portalPath';
import { buildSampleValuesForPage } from '../utils/sampleData';
import NotFoundPage from './NotFoundPage';

const EMPTY_FORM_PAGES: FormPageConfig[] = [];

const REQUIRED_FIELDS_ALERT: FormAlertState = {
  title: 'Please complete all required fields',
  message: 'The form cannot be submitted until every field marked with * has been completed. Missing fields are highlighted below.',
};

interface PendingVoucherDocuments {
  response: VoucherDocumentsResponse;
  valuesKey: string;
  files: Record<string, File[]>;
}

function sameSelectedFiles(left: Record<string, File[]>, right: Record<string, File[]>): boolean {
  const fieldIds = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const fieldId of fieldIds) {
    const leftFiles = left[fieldId] ?? [];
    const rightFiles = right[fieldId] ?? [];
    if (leftFiles.length !== rightFiles.length || leftFiles.some((file, index) => file !== rightFiles[index])) {
      return false;
    }
  }
  return true;
}

function VoucherSection({
  section,
  isMobile,
  onFilesSelected,
  documentMode,
}: {
  section: SectionSchema;
  isMobile: boolean;
  onFilesSelected: (fieldId: string, files: File[]) => File[];
  documentMode: boolean;
}) {
  const columns = isMobile ? 1 : section.columns ?? 1;
  return (
    <Paper
      component="section"
      p="lg"
      withBorder
      radius="md"
      className="portal-panel"
    >
      <Title order={4} mb={section.description ? 'xs' : 'md'} className="portal-section-title">
        {section.title}
      </Title>
      {section.description && !documentMode && <Text size="sm" c="dimmed" mb="md">{section.description}</Text>}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 'var(--mantine-spacing-sm)' }}>
        {section.fields.map((field) => (
          <div
            key={field.id}
            data-field-id={field.id}
            style={{ gridColumn: `span ${Math.min(field.span ?? 1, columns)}` }}
          >
            <FormField
              field={field}
              onFilesSelected={onFilesSelected}
              variant={documentMode ? 'voucherDocument' : 'default'}
            />
          </div>
        ))}
      </div>
    </Paper>
  );
}

function withVoucherTheme(config: ProgramConfig, theme: ThemeConfig | undefined): ProgramConfig {
  return theme ? {
    ...config,
    theme: {
      ...config.theme,
      ...theme,
      colors: { ...config.theme.colors, ...theme.colors },
    },
  } : config;
}

function initialValues(pages: FormPageConfig[]): Record<string, unknown> {
  return Object.fromEntries(
    pages.flatMap((page) => page.sections.flatMap((section) => section.fields))
      .filter((field) => field.defaultValue !== undefined)
      .map((field) => [field.id, field.defaultValue]),
  );
}

function valuesForPages(pages: FormPageConfig[], values: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    pages.flatMap((page) => page.sections.flatMap((section) => section.fields))
      .filter((field) => field.id in values)
      .map((field) => [field.id, values[field.id]]),
  );
}

export default function VoucherPage() {
  const { product = '' } = useParams<{ product: string }>();
  const productKey = product.toLowerCase();
  const { data: config, isLoading, error, sessionScopeKey } = useResolvedProgram();
  const sessionQuery = useProtectedSession(sessionScopeKey);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 48em)') ?? false;
  const [alert, setAlert] = useState<FormAlertState>();
  const [files, setFiles] = useState<Record<string, File[]>>({});
  const [pendingDocumentResponse, setPendingDocumentResponse] = useState<PendingVoucherDocuments>();
  const alertRef = useRef<HTMLDivElement>(null);
  // Field IDs with server-side errors, cleared before each submit so a stale server error
  // can't block the next attempt.
  const serverErrorFieldIds = useRef<Set<string>>(new Set());
  const logoutMutation = useMutation({ mutationFn: logoutSession });

  const productConfig = config?.products?.[productKey];
  const productPages = productConfig?.pages.form ?? EMPTY_FORM_PAGES;
  const documentConfig = config?.voucherSubmission?.documents;
  const documentPages = documentConfig?.pages.form ?? EMPTY_FORM_PAGES;
  const pages = useMemo(() => [...productPages, ...documentPages], [documentPages, productPages]);
  const formValues = useMemo(() => initialValues(pages), [pages]);
  const methods = useForm<Record<string, unknown>>({ values: formValues });
  const watchedValues = useWatch({ control: methods.control });
  const documentValuesKey = JSON.stringify(valuesForPages(documentPages, watchedValues ?? {}));
  const voucherState = location.state as { voucherPage?: number; submissionResultId?: string } | null;
  const requestedPageIndex = voucherState?.voucherPage ?? 0;
  const pageIndex = Math.min(Math.max(requestedPageIndex, 0), Math.max(pages.length - 1, 0));
  const showDocuments = documentPages.length > 0 && pageIndex >= productPages.length;
  const showSampleData = isDevelopmentEnvironment() && pageIndex === 1;

  useEffect(() => {
    setPendingDocumentResponse((pending) => pending && pending.valuesKey !== documentValuesKey ? undefined : pending);
  }, [documentValuesKey]);

  // Form values live only in memory, but the page index survives a reload (history state). Landing
  // on a later product page without having come through the earlier ones in this visit means their
  // answers are gone, so start over at the first page. The documents page only needs the
  // submission result ID, so it is left alone.
  const pageEntryChecked = useRef(false);
  useEffect(() => {
    if (pageEntryChecked.current || productPages.length === 0) return;
    pageEntryChecked.current = true;
    if (pageIndex > 0 && !showDocuments) {
      navigate('.', { replace: true, state: null });
    }
  }, [navigate, pageIndex, productPages.length, showDocuments]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pageIndex]);

  useEffect(() => {
    if (alert) {
      alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [alert]);

  if (isLoading || sessionQuery.isLoading) {
    return <Center h="100vh"><Loader size="lg" /></Center>;
  }
  if (error || !config || !config.voucherSubmission || !productConfig || productPages.length === 0) {
    return <NotFoundPage />;
  }
  if (sessionQuery.isUnauthorized) {
    return <Navigate to="/auth/request" state={{ returnTo: buildReturnToPath(location) }} replace />;
  }
  if (sessionQuery.isError) {
    return <Alert color="red" title="Session Error">Unable to verify your session.</Alert>;
  }

  const activePage = pages[Math.min(pageIndex, pages.length - 1)];
  const pageConfig = showDocuments ? documentConfig!.pages : productConfig!.pages;
  const themedConfig = withVoucherTheme(config, productConfig?.theme);
  const clearServerErrors = () => {
    if (serverErrorFieldIds.current.size > 0) {
      methods.clearErrors([...serverErrorFieldIds.current]);
      serverErrorFieldIds.current.clear();
    }
  };
  const navigateToPage = (nextPageIndex: number) => {
    setAlert(undefined);
    clearServerErrors();
    navigate('.', { state: { ...voucherState, voucherPage: nextPageIndex } });
  };
  const handleFilesSelected = (fieldId: string, selectedFiles: File[]) => {
    setPendingDocumentResponse(undefined);
    setFiles((current) => ({ ...current, [fieldId]: selectedFiles }));
    return selectedFiles;
  };
  const handleFillSampleData = () => {
    methods.reset({
      ...methods.getValues(),
      ...buildSampleValuesForPage(activePage),
    });
    methods.clearErrors();
  };
  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } finally {
      if (sessionScopeKey) clearSessionExpiry(sessionScopeKey);
      navigate('/auth/request', { replace: true });
    }
  };
  const showError = (message: string) => setAlert({ ...systemErrorAlert(), message });
  // Highlights server field errors on this page. Errors on another page can't be highlighted (and
  // would silently block the next submit if set), so those just send the user back to start over.
  // Returns false when the error carries no field errors.
  const applyFieldErrors = (requestError: unknown): boolean => {
    if (!(requestError instanceof VoucherApiError) || !requestError.fieldErrors) return false;
    const entries = Object.entries(requestError.fieldErrors);
    if (entries.length === 0) return false;
    const activeFieldIds = new Set(activePage.sections.flatMap((section) => section.fields.map((field) => field.id)));
    let otherPageError = false;
    entries.forEach(([fieldId, message]) => {
      if (activeFieldIds.has(fieldId.split('.')[0])) {
        methods.setError(fieldId, { type: 'server', message });
        serverErrorFieldIds.current.add(fieldId);
      } else {
        otherPageError = true;
      }
    });
    setAlert(otherPageError
      ? {
        title: 'Please start the form again',
        message: 'Use Back to return to the first page and complete the form again.',
      }
      : {
        title: 'Please correct the highlighted fields',
        message: 'Some of the information entered could not be accepted. Correct the highlighted fields below and submit again.',
      });
    return true;
  };
  const applyError = (requestError: unknown) => {
    if (applyFieldErrors(requestError)) return;
    // Server messages are not written for users, except the one telling them not to retry. Our own
    // upload errors (ApiError, e.g. "Upload failed for x.pdf") are; a network or CORS failure
    // (TypeError "Failed to fetch") is not.
    const userFacing = requestError instanceof VoucherApiError
      ? requestError.message === VOUCHER_NOT_RECORDED_ERROR
      : requestError instanceof ApiError;
    if (userFacing) {
      showError((requestError as Error).message);
    } else {
      setAlert(systemErrorAlert());
    }
  };
  const submit = async (values: Record<string, unknown>) => {
    try {
      if (showDocuments) {
        if (!voucherState?.submissionResultId) {
          showError('Complete the voucher submission before submitting documents.');
          return;
        }
        const documentValues = valuesForPages(documentPages, values);
        const valuesKey = JSON.stringify(documentValues);
        const reusableResponse = pendingDocumentResponse
          && pendingDocumentResponse.valuesKey === valuesKey
          && sameSelectedFiles(pendingDocumentResponse.files, files)
          ? pendingDocumentResponse.response
          : undefined;
        const response = reusableResponse
          ?? await submitVoucherDocuments(voucherState.submissionResultId, documentValues, files);
        setPendingDocumentResponse({
          response,
          valuesKey,
          files: Object.fromEntries(Object.entries(files).map(([fieldId, selectedFiles]) => [fieldId, [...selectedFiles]])),
        });
        await uploadVoucherDocuments(response.uploads, files);
        setPendingDocumentResponse(undefined);
        navigate(`/voucher-submissions/result/${response.result_id}`);
        return;
      }
      if (activePage.action === 'validateEligibility') {
        // A failed check (non-2xx, network error) is a system problem, not an answer about the
        // patient, so it always gets the generic message; the raw error is never shown.
        let eligibility;
        try {
          eligibility = await validateVoucherEligibility(productKey, values);
        } catch (eligibilityError) {
          if (!applyFieldErrors(eligibilityError)) {
            setAlert(systemErrorAlert());
          }
          return;
        }
        if (!eligibility.eligible) {
          setAlert({
            title: pageConfig.ineligible?.title ?? 'Patient Not Eligible',
            message: pageConfig.ineligible?.message ?? 'The patient is not eligible for this voucher.',
          });
          return;
        }
        if (pageIndex < productPages.length - 1) {
          navigateToPage(pageIndex + 1);
          return;
        }
      }
      const response = await submitVoucherSubmission(productKey, values);
      if (documentPages.length > 0) {
        navigate('.', { state: { voucherPage: productPages.length, submissionResultId: response.result_id } });
        return;
      }
      navigate(`/voucher-submissions/result/${response.result_id}`);
    } catch (requestError) {
      applyError(requestError);
    }
  };
  const handleInvalidSubmit = (errors: FieldErrors<Record<string, unknown>>) => {
    setAlert(REQUIRED_FIELDS_ALERT);
    const firstFieldId = Object.keys(errors)[0];
    if (firstFieldId) {
      document.querySelector<HTMLElement>(`[data-field-id="${firstFieldId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Every submit starts clean: the previous alert and server field errors go away before validating.
  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    setAlert(undefined);
    clearServerErrors();
    return methods.handleSubmit(submit, handleInvalidSubmit)(event);
  };

  const formHeader = (
    <Group justify="space-between" align="center">
      <Text size="xs" c="dimmed"><Text component="span" c="red">*</Text> indicates required fields</Text>
      {showSampleData && (
        <Button type="button" variant="subtle" size="compact-xs" p={0} onClick={handleFillSampleData} disabled={methods.formState.isSubmitting}>
          Fill Sample Data
        </Button>
      )}
    </Group>
  );
  const formBody = (
    <Stack gap="lg">
      {!showDocuments && formHeader}
      <div ref={alertRef}>
        {alert && (
          <Alert
            color="red"
            icon={<IconAlertOctagonFilled />}
            title={<Text c="#80170c" fw={700}>{alert.title}</Text>}
            variant="light"
          >
            <Text c="#80170c">{alert.message}</Text>
          </Alert>
        )}
      </div>
      {activePage.sections.map((section) => (
        <VoucherSection
          key={section.id}
          section={section}
          isMobile={isMobile}
          onFilesSelected={handleFilesSelected}
          documentMode={showDocuments}
        />
      ))}
      <Group justify="flex-end">
        {pageIndex > 0 && <Button type="button" variant="default" onClick={() => navigateToPage(pageIndex - 1)} disabled={methods.formState.isSubmitting}>Back</Button>}
        <Button type="button" variant="default" onClick={() => navigate('/')} disabled={methods.formState.isSubmitting}>Cancel</Button>
        <Button type="submit" className="portal-primary-button" loading={methods.formState.isSubmitting}>
          {activePage.submitLabel ?? (pageIndex < pages.length - 1 ? 'Continue' : 'Submit')}
        </Button>
      </Group>
    </Stack>
  );

  return (
    <PageShell
      config={themedConfig}
      headerEyebrow={showDocuments ? pageConfig.eyebrow : undefined}
      headerTitle={pageConfig.title ?? themedConfig.theme.displayName}
      headerSubtitle={showDocuments ? pageConfig.subtitle : undefined}
      headerSubtitlePadding={showDocuments ? 0 : undefined}
      sessionEmail={sessionQuery.data?.email}
      onGoHome={() => navigate('/')}
      onLogout={() => { void handleLogout(); }}
      isLoggingOut={logoutMutation.isPending}
      support={activePage.showSupportInfo === true ? config.support : undefined}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleFormSubmit} noValidate>
          {showDocuments ? (
            <Stack gap="sm">
              {formHeader}
              {formBody}
            </Stack>
          ) : formBody}
        </form>
      </FormProvider>
    </PageShell>
  );
}